<template>
  <div>
    <v-container fluid>
      <TimePairFilterBar
        :timePairs="filterableTimePairs"
        manager
        @filter-added="clearSelectedTimePairs"
        @filter-removed="clearSelectedTimePairs"
        @status-filter-changed="loadApprovals"
        @filter-approval-dates="filterApprovalDates"
      />

      <v-data-table
        id="approvalsTable"
        disable-pagination
        show-select
        show-expand
        hide-default-footer
        disable-sort
        :headers="parentHeaders"
        :headers-length="activeHeaders.length"
        :items="approvals"
        :loading="!isMobile() && approvalsLoading"
        :no-data-text="`${$t('tables.noData')}`"
        :expanded="expanded"
        class="elevation-1 mb-14"
      >
        <template v-slot:top v-if="isTimePairApprovalFeatureActive">
          <v-card class="px-4 py-2 my-1 sticky" :style="isMobile() ? 'top: 56px' : 'top: 65px'">
            <v-card-title class="body-1 pa-0">
              <span v-if="!isXsViewport()" class="ml-2"> {{ $t('titles.approvalSelections') }}</span>

              <v-chip
                :disabled="multiApprovalDisabled"
                close
                class="ma-2"
                @click:close="clearSelectedTimePairs"
              >
                {{ getSelectedTimePairs.length + ` ${$t('misc.selected')}` }}
              </v-chip>

              <v-switch
                v-if="!isXsViewport() && roundingEnabled"
                v-model="viewAdjustedTime"
                color="secondary"
                :label="`${$t('titles.adjustedTime')}`"
                @click="updateTableHeaders()"
              />

              <v-spacer/>
              <v-btn
                :disabled="multiApprovalDisabled"
                :outlined="multiApprovalDisabled"
                color="error"
                class="mr-3"
                @click="multiTimePairApproval(false)"
              >
                {{ !isXsViewport() ? $t('actions.reject') : '' }}
                <v-icon :right="!isXsViewport()">close</v-icon>
              </v-btn>
              <v-btn
                :disabled="multiApprovalDisabled"
                :outlined="multiApprovalDisabled"
                color="success"
                @click="multiTimePairApproval(true)"
              >
                {{ !isXsViewport() ? $t('actions.approve') : '' }}
                <v-icon :right="!isXsViewport()">done</v-icon>
              </v-btn>

              <v-switch
                v-if="isXsViewport() && roundingEnabled"
                v-model="viewAdjustedTime"
                color="secondary"
                :label="`${$t('titles.adjustedTime')}`"
              />
            </v-card-title>
          </v-card>
        </template>

        <template v-slot:header.data-table-select>
          <v-checkbox
            v-if="isTimePairApprovalFeatureActive"
            v-model="allCurrentTimePairsSelected"
            :disabled="getSelectableApprovals.length === 0"
            :label="isMobile() ? `${ $t('tables.select') }` : ''"
            ripple
            hide-details
            class="mt-0 pt-0 subtitle-2"
          />
        </template>

        <template v-slot:item="{ item, expand, isExpanded, index }">
          <tr
            v-if="getFilteredTimes(item.summaries).length > 0"
            class="v-row-group__header"
          >
            <td :class="isMobile() ? 'px-2' : ''">
              <v-btn icon @click="expand(!isExpanded)">
                <v-icon>
                  {{ isExpanded ? 'keyboard_arrow_down' : 'keyboard_arrow_right' }}
                </v-icon>
              </v-btn>
            </td>
            <td :class="isMobile() ? 'pl-0 pr-2' : ''">
              <v-checkbox
                v-if="isTimePairApprovalFeatureActive"
                v-model="allCurrentEmployeePairsSelected[item.id]"
                :disabled="getSelectableApprovalsByUsername(item.id).length === 0"
                ripple
                hide-details
                class="mt-0 pt-0"
                @change="handleEmployeeSelectAll(item, $event)"
              />
            </td>
            <td :class="isMobile() ? 'px-0' : ''" style="width: 100px">
              {{ item.summaries[0].employeeAssignment.id }}
            </td>
            <td :class="isMobile() ? 'px-0' : ''">
              <v-row class="ma-0">
                <v-col :cols="$vuetify.breakpoint.smAndUp ? 8 : 12" class="pa-0 ma-0">
                  <v-layout align-center fill-height class="pa-0 ma-0">
                    {{ item.firstName }} {{ item.lastName }}
                  </v-layout>
                </v-col>
                <v-col :cols="$vuetify.breakpoint.smAndUp ? 4 : 12" class="pa-0 ma-0">
                  <v-layout align-center fill-height :justify-center="$vuetify.breakpoint.smAndUp" class="pa-0 ma-0">
                    ({{ getEmployeeDuration(item.summaries) }})
                  </v-layout>
                </v-col>
              </v-row>
            </td>
            <td :class="isMobile() ? 'pr-0 pl-1' : ''">
              <TimePairStatusIndicator
                :items="getOverallStatusIndicators(item.summaries)"
              />
            </td>
            <td :class="isMobile() ? 'px-1' : ''" class="text-no-wrap">
              <v-tooltip top :disabled="isMobile()">
                <template v-slot:activator="{ on }">
                  <v-icon
                    v-if="isTimePairAddFeatureActive"
                    v-on="on"
                    class="mx-2"
                    @click="addTimePair(index)"
                  >
                    alarm_add
                  </v-icon>
                </template>
                <span>{{ $t('actions.addTime') }}</span>
              </v-tooltip>
              <v-tooltip top :disabled="isMobile()">
                <template v-slot:activator="{ on }">
                  <v-icon
                    v-if="isTimePairApprovalFeatureActive"
                    v-on="on"
                    :disabled="isParentApprovalDisabled(item)"
                    class="mx-2"
                    @click="singleParentApproval(item, false)"
                  >
                    cancel
                  </v-icon>
                </template>
                <span>{{ $t('actions.rejectAll') }}</span>
              </v-tooltip>
              <v-tooltip top :disabled="isMobile()">
                <template v-slot:activator="{ on }">
                  <v-icon
                    v-if="isTimePairApprovalFeatureActive"
                    v-on="on"
                    :disabled="isParentApprovalDisabled(item)"
                    class="mx-2"
                    @click="singleParentApproval(item, true)"
                  >
                    done_all
                  </v-icon>
                </template>
                <span>{{ $t('actions.approveAll') }}</span>
              </v-tooltip>
              <div
                v-if="!isTimePairAddFeatureActive && !isTimePairApprovalFeatureActive"
                class="text--disabled font-italic"
              >
                {{ $t('misc.unavailable') }}
              </div>
            </td>
          </tr>
        </template>

        <template v-slot:expanded-item="{ item }">
          <td :colspan="activeHeaders.length"
              class="px-2 approvals-expanded-item"
          >
            <ApprovalsTimePairTable
              :username="item.id"
              :viewAdjustedTime="viewAdjustedTime"
              :headers="activeHeaders"
              @add-segment="addTimePair(null, $event)"
              @edit-time-pair="editTimePair"
              @view-time-pair="isTimePairEditFeatureActive ? viewTimePairHistory($event) : viewTimePairDetails($event)"
              @view-segments="viewSegmentTimePairs"
              @view-time-pair-map="viewTimePairMap"
              @single-time-pair-approval="singleTimePairApproval"
              @clock-out="clockOutIncompleteTimePair"
            />
          </td>
        </template>
      </v-data-table>
    </v-container>

    <v-bottom-navigation fixed grow dark background-color="primary">
      <v-card v-if="!isMenuDrawerClosable()" flat disabled width="256px" color="secondary"/>

      <v-btn v-if="isTimePairAddFeatureActive" @click="addTimePair(-1)">
        <span>{{ $t('actions.addTime') }}</span>
        <v-icon>alarm_add</v-icon>
      </v-btn>

      <v-spacer v-if="!isXsViewport()"/>

      <v-btn v-if="isTimePairApprovalFeatureActive" @click="globalTimePairApproval(false)">
        <span>{{ $t('actions.rejectAll') }}</span>
        <v-icon>cancel</v-icon>
      </v-btn>
      <v-btn v-if="isTimePairApprovalFeatureActive" @click="globalTimePairApproval(true)">
        <span>{{ $t('actions.approveAll') }}</span>
        <v-icon>done_all</v-icon>
      </v-btn>
    </v-bottom-navigation>

    <TimePairDialog
      :value="timePairAddDialog"
      :shiftTimePair="newTimePairShift"
      :subordinateEmployees="subordinateEmployees"
      :selectedSubordinate="newTimePairSubordinate"
      @close="closeAddTimePair"
    />

    <TimePairDialog
      :value="timePairEditDialog"
      :timePair="editingTimePair"
      :shiftTimePair="editingTimePairShift"
      :subordinateEmployees="subordinateEmployees"
      :selectedSubordinate="editingTimePairSubordinate"
      @close="closeEditTimePair"
    />

    <TimePairHistoryDialog
      :value="timePairHistoryDialog"
      :timePair="historyViewTimePair"
      @view-time-pair="viewTimePairDetails"
      @close="timePairHistoryDialog = false"
    />

    <TimePairDetailsDialog
      :value="timePairViewDialog"
      :timePair="viewingTimePair"
      :previousVersion="viewingTimePairPreviousVersion"
      @close="timePairViewDialog = false"
    />

    <TimePairMapDialog
      :value="timePairMapDialogActive"
      :timePair="viewingTimePair"
      @close="timePairMapDialogActive = false"
    />

    <SegmentTimePairTableDialog
      v-if="isMobile()"
      :value="segmentTimePairViewDialog"
      :headers="segmentTimeDataTableHeaders"
      :items="filterDeletedOvertimeSegments(viewingSegmentTimePairs)"
      @close="closeViewSegmentTimePairs"
    >
      <template v-if="isSegmentTimePairFeatureActive" v-slot:subtitle-actions>
        <v-btn
          v-if="isTimePairAddFeatureActive"
          v-show="viewingSegmentTimePairsShift.clockOut && !viewingSegmentTimePairsShift.isInactive"
          color="primary"
          class="mr-2"
          @click="addTimePair(null, viewingSegmentTimePairsShift)"
        >
          {{ $t('actions.addBreak') }}
          <v-icon right>add</v-icon>
        </v-btn>
      </template>
      <template
        v-if="isSegmentTimePairFeatureActive && viewingSegmentTimePairsShift.clockOut"
        v-slot:actions="{ item }"
      >
        <div v-if="isTimePairEditFeatureActive && item.clockOut && !item.isInactive
        && !isOvertimeRelatedSegment(item)">
          <v-icon
            :class="actionIcon"
            @click="editTimePair({ shiftTimePair: viewingSegmentTimePairsShift, segmentTimePair: item })"
          >
            edit
          </v-icon>
        </div>
        <div
          v-else
          class="font-italic text--disabled"
        >
          {{ $t('misc.unavailable') }}
        </div>
      </template>
    </SegmentTimePairTableDialog>
  </div>
</template>

<script>
import {
  mapActions, mapGetters, mapMutations, mapState,
} from 'vuex';

import filter from 'lodash/filter';
import find from 'lodash/find';
import findIndex from 'lodash/findIndex';
import flatMap from 'lodash/flatMap';
import forEach from 'lodash/forEach';
import isEmpty from 'lodash/isEmpty';
import isNull from 'lodash/isNull';
import keys from 'lodash/keys';
import some from 'lodash/some';

import DateTimeUtil from '@/util/date-time-util';
import constants from '@/constants';

import canClockShiftOut from '@/mixins/clocking/individual/canClockShiftOut';
import displayAdaptable from '@/mixins/displayAdaptable';
import getTimePairClockingType from '@/mixins/timepairs/getTimePairClockingType';
import getTimePairStatusIndicator from '@/mixins/timepairs/getTimePairStatusIndicator';
import hasAppSnackbar from '@/mixins/hasAppSnackbar';
import hasActionIcon from '@/mixins/hasActionIcon';
import hasAppDialog from '@/mixins/hasAppDialog';
import hasAppLoader from '@/mixins/hasAppLoader';
import hasLocationData from '@/mixins/location/hasLocationData';
import hasLocationFeature from '@/mixins/location/hasLocationFeature';
import hasTimePairFeatures from '@/mixins/timepairs/hasTimePairFeatures';
import resolveMobileBackButton from '@/mixins/resolveMobileBackButton';
import hasWorkScheduleFeatures from '@/mixins/hasWorkScheduleFeatures';
import overtimeSegments from '@/mixins/overtimeSegments';

import ApprovalsTimePairTable from '@/components/approvals/ApprovalsTimePairTable.vue';
import SegmentTimePairTableDialog from '@/components/timepairs/SegmentTimePairTableDialog.vue';
import TimePairDetailsDialog from '@/components/timepairs/TimePairDetailsDialog.vue';
import TimePairDialog from '@/components/timepairs/TimePairDialog.vue';
import TimePairFilterBar from '@/components/timepairs/TimePairFilterBar.vue';
import TimePairHistoryDialog from '@/components/timepairs/TimePairHistoryDialog.vue';
import TimePairMapDialog from '@/components/timepairs/TimePairMapDialog.vue';
import TimePairStatusIndicator from '@/components/timepairs/TimePairStatusIndicator.vue';
import hasAssignmentFeatures from '@/mixins/clocking/hasAssignmentFeatures';

export default {
  name: 'Approvals',

  mixins: [
    canClockShiftOut,
    displayAdaptable,
    getTimePairClockingType,
    getTimePairStatusIndicator,
    hasActionIcon,
    hasAppDialog,
    hasAppLoader,
    hasLocationData,
    hasLocationFeature,
    hasAssignmentFeatures,
    hasAppSnackbar,
    hasTimePairFeatures,
    resolveMobileBackButton,
    hasWorkScheduleFeatures,
    overtimeSegments,
  ],

  components: {
    TimePairDetailsDialog,
    SegmentTimePairTableDialog,
    TimePairFilterBar,
    ApprovalsTimePairTable,
    TimePairHistoryDialog,
    TimePairDialog,
    TimePairMapDialog,
    TimePairStatusIndicator,
  },

  data() {
    return {
      approvalStatusFilter: [],
      viewAdjustedTime: false,
      parentHeaders: [
        {
          text: `${this.$t('tableHeaders.employeeId')}`,
          value: constants.DataTableHeaderValues.EMPLOYEE_ID,
          width: '200px',
        },
        {
          text: `${this.$t('tableHeaders.name')}`,
          value: constants.DataTableHeaderValues.NAME,
          width: '200px',
        },
        {
          text: `${this.$t('tableHeaders.info')}`,
          value: constants.DataTableHeaderValues.INFO,
        },
        {
          text: this.$t('tableHeaders.actions'),
          value: constants.DataTableHeaderValues.ACTIONS,
          align: 'end',
          width: '50px',
        },
      ],
      headers: [
        {
          text: '',
          value: 'data-table-expand',
          align: 'start',
          sortable: false,
          width: '70px',
          inactive: () => this.isMobile(),
        },
        {
          text: this.isMobile() ? this.$t('tables.select') : '',
          value: constants.DataTableHeaderValues.DATA_TABLE_SELECT,
          sortable: false,
        },
        {
          text: `${this.$t('tableHeaders.info')}`,
          value: constants.DataTableHeaderValues.INFO,
          sortable: false,
          width: this.isMenuDrawerClosable() ? '80px' : '',
        },
        {
          text: `${this.$t('tableHeaders.position')}`,
          value: constants.DataTableHeaderValues.POSITION,
          sortable: true,
          sort: this.sortByPositionDescription,
          inactive: () => !this.isConcurrentEmploymentActive,
        },
        {
          text: `${this.$t('tableHeaders.startDate')}`,
          value: constants.DataTableHeaderValues.START_DATE,
          sortable: true,
          sort: this.sortByDateText,
        },
        {
          text: `${this.$t('clock.clockIn')}`,
          value: constants.DataTableHeaderValues.CLOCK_IN,
          sortable: true,
          sort: this.sortByDateText,
        },
        {
          text: `${this.$t('clock.clockOut')}`,
          value: constants.DataTableHeaderValues.CLOCK_OUT,
          sortable: true,
          sort: this.sortByDateText,
        },
        {
          text: this.$t('tableHeaders.duration'),
          value: constants.DataTableHeaderValues.DURATION,
          align: 'center',
          sortable: true,
          sort: this.sortByDuration,
          width: this.isMenuDrawerClosable() ? '95px' : '',
        },
        {
          text: this.$t('tableHeaders.expectedDuration'),
          value: constants.DataTableHeaderValues.EXPECTED_DURATION,
          align: 'center',
          sortable: false,
          inactive: () => !this.isWorkScheduleManagerViewFeatureEnabled,
        },
        {
          text: 'Status',
          value: constants.DataTableHeaderValues.STATUS,
          align: 'center',
          sortable: true,
          inactive: () => !this.isTimePairApprovalFeatureActive,
        },
        {
          text: `${this.$t('tableHeaders.actions')}`,
          value: constants.DataTableHeaderValues.ACTIONS,
          align: 'center',
          sortable: false,
          width: '330px',
        },
        {
          text: '',
          value: constants.DataTableHeaderValues.APPROVALS,
          sortable: false,
          inactive: () => !this.isMobile(),
        },
        {
          text: '',
          value: 'data-table-expand',
          align: 'start',
          sortable: false,
          width: '70px',
          inactive: () => !this.isMobile(),
        },
      ],
      segmentTimeDataTableHeaders: [
        {
          text: 'Info',
          value: constants.DataTableHeaderValues.INFO,
        },
        {
          text: this.$t('clock.startTime'),
          value: constants.DataTableHeaderValues.CLOCK_IN,
        },
        {
          text: this.$t('clock.endTime'),
          value: constants.DataTableHeaderValues.CLOCK_OUT,
        },
        {
          text: 'Duration',
          value: constants.DataTableHeaderValues.DURATION,
        },
        {
          text: 'Actions',
          value: constants.DataTableHeaderValues.ACTIONS,
          align: 'center',
        },
      ],
      approvalsLoading: false,
      timePairsToSubmit: [],
      timePairsToSubmitStatus: Boolean,
      newTimePairShift: {},
      newTimePairSubordinate: {},
      viewingTimePair: {},
      viewingTimePairPreviousVersion: {},
      historyViewTimePair: {},
      viewingSegmentTimePairs: [],
      viewingSegmentTimePairsShift: {},
      editingTimePair: {},
      editingTimePairShift: {},
      editingTimePairSubordinate: {},
      timePairAddDialog: false,
      timePairEditDialog: false,
      timePairViewDialog: false,
      timePairHistoryDialog: false,
      timePairMapDialogActive: false,
      segmentTimePairViewDialog: false,
      expanded: [],
      dateRange: [],
    };
  },

  computed: {
    ...mapState([
      'employee',
      'geofence',
      'subordinateEmployees',
      'activeFilters',
      'approvalsByUsername',
      'allCurrentEmployeePairsSelected',
      'approvalStatusConfiguration',
    ]),

    ...mapGetters([
      'approvals',
      'getFilteredTimes',
      'getSelectedTimePairs',
      'getSelectableApprovals',
      'getSelectableApprovalsByUsername',
    ]),

    filterableTimePairs() {
      return flatMap(this.approvals, (approval) => approval.summaries);
    },

    multiApprovalDisabled() {
      return this.getSelectedTimePairs.length <= 0;
    },

    activeHeaders() {
      return filter(this.headers, (header) => (header.inactive ? !header.inactive() : true));
    },

    allCurrentTimePairsSelected: {
      get() {
        return this.$store.state.allCurrentTimePairsSelected;
      },
      set(value) {
        this.$store.dispatch('selectAllCurrentlySelectableTimePairs', value);
      },
    },

    roundingEnabled() {
      let rounding = false;
      const employees = Object.keys(this.approvalsByUsername);
      employees.forEach((employee) => {
        if (!rounding
          && this.approvalsByUsername[employee].summaries.length > 0
          && this.approvalsByUsername[employee].summaries[0].rounding) {
          rounding = this.approvalsByUsername[employee].summaries[0].rounding;
        }
      });

      return rounding;
    },
  },

  watch: {
    activeFilters() {
      this.collapseNonDisplayedRows();
    },
  },

  methods: {
    ...mapActions([
      'loadManagerApprovals',
      'loadSubordinateEmployees',
      'postApprovals',
      'selectCurrentlySelectablePairsByUsername',
      'updateAllGlobalCheckboxes',
      'clearSelectedTimePairs',
      'loadWorkSchedules',
      'loadDefaultApprovalStatusFilter',
    ]),

    ...mapMutations([
      'setPageTitle',
      'updateApprovals',
      'filterApprovalsByDate',
    ]),

    getEmployeeDuration(timePairSummaries) {
      let totalDurationInMinutes = 0;

      forEach(filter(timePairSummaries, (summary) => !summary.isInactive), (summary) => {
        totalDurationInMinutes += summary.roundedDuration;
      });

      return DateTimeUtil.getFormattedDuration(totalDurationInMinutes);
    },

    getOverallStatusIndicators(timePairSummaries) {
      const indicators = [];

      if (some(timePairSummaries, (timePairSummary) => !timePairSummary.clockOut)) {
        indicators.push(this.getIndicatorObject(
          'clock-in', 'alarm', `${constants.AppColors.SUCCESS} darken-3`, this.$t('indicators.clockedIn'),
        ));
      }
      if (some(timePairSummaries, (timePairSummary) => timePairSummary.isManualPair)) {
        indicators.push(this.getIndicatorObject(
          'manual', 'note_add', constants.AppColors.WARN, this.$t('indicators.containsManualTime'),
        ));
      }
      if (some(timePairSummaries, (timePairSummary) => timePairSummary.isCorrection)) {
        indicators.push(this.getIndicatorObject(
          'correction', 'edit', constants.AppColors.WARN, this.$t('indicators.containsCorrections'),
        ));
      }
      if (this.isLocationFeatureActive
        && some(timePairSummaries, (timePairSummary) => !this.hasLocationData(timePairSummary))) {
        indicators.push(this.getIndicatorObject(
          'missing-geo-data', 'location_off', constants.AppColors.WARN, this.$t('indicators.missingGeoData'),
        ));
      }
      if (this.isLocationFeatureActive && some(timePairSummaries, (timePairSummary) => !isEmpty(
        this.geofence.flatMap((geoFenceAssignment) => geoFenceAssignment.geofences),
      ) && (timePairSummary.clockIn.geofenceResult === constants.LocationData.GeofenceResult.BORDERING_INSIDE
          || (timePairSummary.clockOut
            && !timePairSummary.clockOut.geofenceResult
            === constants.LocationData.GeofenceResult.BORDERING_OUTSIDE)))) {
        indicators.push(this.getIndicatorObject(
          'outside-geofence', 'gps_off', constants.AppColors.WARN, 'Entries Outside Geofence',
        ));
      }

      if (some(timePairSummaries, (timePairSummary) => (
        (timePairSummary.hasTimeZoneDiscrepancy || timePairSummary.hasShiftTimeZoneDiscrepancy)
        && timePairSummary.clockOut
      ))) {
        indicators.push(this.getIndicatorObject(
          'time_zone_discrepancy',
          'public',
          constants.AppColors.WARN,
          this.$t('indicators.shiftInDifferentTimeZone'),
        ));
      }

      return indicators;
    },

    isParentApprovalDisabled(approval) {
      return filter(approval.summaries, (summary) => !isNull(summary.clockOut)).length === 0;
    },

    handleEmployeeSelectAll(employee, value) {
      this.selectCurrentlySelectablePairsByUsername({ username: employee.id, value });
    },

    singleTimePairApproval(timePair, isApproved) {
      this.confirmApprovalAction(
        [timePair],
        isApproved,
        `${this.$t('dialogs.confirm.actionThisTime',
          {
            action: isApproved ? this.$t('actions.approve').toUpperCase()
              : this.$t('actions.reject').toUpperCase(),
          })}`,
      );
    },

    singleParentApproval(approval, isApproved) {
      const timePairs = filter(
        approval.summaries, (summary) => (!isNull(summary.clockOut)
          && summary.status !== constants.Approvals.Status.APPROVED
          && summary.status !== constants.Approvals.Status.REJECTED),
      );

      this.confirmApprovalAction(
        timePairs,
        isApproved,
        this.$t('dialogs.confirm.actionAllTimesForEmp', {
          action: isApproved ? this.$t('actions.approve').toUpperCase() : this.$t('actions.reject').toUpperCase(),
          employee: `${approval.firstName} ${approval.lastName}`,
        }),
      );
    },

    multiTimePairApproval(isApproved) {
      this.confirmApprovalAction(
        this.getSelectedTimePairs,
        isApproved,
        this.$tc('dialogs.confirm.actionSelectedItems', this.getSelectedTimePairs.length, {
          action: isApproved ? this.$t('actions.approve').toUpperCase() : this.$t('actions.reject').toUpperCase(),
        }),
      );
    },

    globalTimePairApproval(isApproved) {
      let timePairs = [];
      forEach(this.approvals, (approval) => {
        timePairs = timePairs.concat(filter(approval.summaries,
          (summary) => !isNull(summary.clockOut)
            && summary.status !== constants.Approvals.Status.APPROVED
            && summary.status !== constants.Approvals.Status.REJECTED));
      });

      this.confirmApprovalAction(
        timePairs,
        isApproved,
        this.$t('dialogs.confirm.actionAllTimes', {
          action: isApproved ? this.$t('actions.approve').toUpperCase() : this.$t('actions.reject').toUpperCase(),
        }),
      );
    },

    confirmApprovalAction(timePairs, isApproved, message) {
      const status = isApproved ? constants.Approvals.Status.APPROVED : constants.Approvals.Status.REJECTED;

      this.showAppConfirmDialog({
        message,
        color: isApproved ? constants.AppColors.PRIMARY : constants.AppColors.ERROR,
        confirmText: isApproved ? this.$t('actions.approve') : this.$t('actions.reject'),
        confirmHandler: () => this.submitActionedApprovals(timePairs, status),
      });
    },

    submitActionedApprovals(timePairsToSubmit, submissionStatus) {
      if (isEmpty(timePairsToSubmit)) {
        this.showSnackbar({
          text: this.$t('snackbar.noTimes', { status: submissionStatus }),
          color: constants.AppColors.ERROR,
        });
      } else {
        this.showAppLoader(this.$t('appLoader.submittingApprovals'));
        const request = this.buildApprovalRequest(timePairsToSubmit, submissionStatus);

        this.postApprovals(request)
        .then(() => {
          this.showSnackbar({
            text: this.$tc('snackbar.selectedTimeHasBeen', timePairsToSubmit.length, {
              status: this.$t(`status.${submissionStatus}`),
            }),
            color: submissionStatus === constants.Approvals.Status.APPROVED
              ? constants.AppColors.SUCCESS : constants.AppColors.ERROR,
          });
          this.loadManagerApprovals(this.approvalStatusFilter);
        })
        .catch(() => {
          this.showSnackbar({
            text: this.$t('snackbar.selectedTimeCouldNotBe', {
              status: submissionStatus,
            }),
            color: constants.AppColors.ERROR,
          });
        })
        .finally(() => {
          this.updateAllGlobalCheckboxes();
          this.collapseNonDisplayedRows();
          this.hideAppLoader();
        });
      }
    },

    buildApprovalRequest(timePairs, status) {
      const statusRequests = [];

      forEach(timePairs, (timePair) => {
        statusRequests.push({
          timePairId: timePair.id,
          status,
        });
      });

      return {
        requestedByUserId: this.employee.id,
        statusRequests,
      };
    },

    addTimePair(approvalIndex, shiftTimePair) {
      if (this.segmentTimePairViewDialog) this.closeViewSegmentTimePairs();

      this.newTimePairShift = shiftTimePair;

      let approval;
      if (approvalIndex !== undefined && approvalIndex !== null) {
        approval = this.approvals[approvalIndex];
      } else {
        approval = this.getApprovalByTimePair(shiftTimePair);
      }

      this.newTimePairSubordinate = approval ? this.getSubordinateByApprovalId(approval.id) : {};
      this.timePairAddDialog = true;
    },

    closeAddTimePair() {
      this.newTimePairShift = {};
      this.newTimePairSubordinate = {};
      this.timePairAddDialog = false;
    },

    editTimePair(timePairEvent) {
      if (this.segmentTimePairViewDialog) this.closeViewSegmentTimePairs();

      this.editingTimePair = timePairEvent.segmentTimePair ? timePairEvent.segmentTimePair
        : timePairEvent.shiftTimePair;
      this.editingTimePairShift = timePairEvent.segmentTimePair ? timePairEvent.shiftTimePair : {};
      this.editingTimePairSubordinate = this.getSubordinateByApprovalId(
        this.getApprovalByTimePair(timePairEvent.shiftTimePair).id,
      );
      this.timePairEditDialog = true;
    },

    closeEditTimePair() {
      this.editingTimePair = {};
      this.editingTimePairShift = {};
      this.timePairEditDialog = false;
    },

    viewTimePairDetails(timePairEvent) {
      this.viewingTimePair = timePairEvent.timePair || timePairEvent;
      this.viewingTimePairPreviousVersion = timePairEvent.previousVersion || {};
      this.timePairViewDialog = true;
    },

    viewTimePairHistory(timePair) {
      this.historyViewTimePair = timePair;
      this.timePairHistoryDialog = true;
    },

    viewSegmentTimePairs(shiftTimePair) {
      this.viewingSegmentTimePairsShift = shiftTimePair;
      this.viewingSegmentTimePairs = shiftTimePair.segmentTimePairs;
      this.segmentTimePairViewDialog = true;
    },

    closeViewSegmentTimePairs() {
      this.segmentTimePairViewDialog = false;
      this.viewingSegmentTimePairsShift = {};
      this.viewingSegmentTimePairs = [];
    },

    viewTimePairMap(timePair) {
      this.viewingTimePair = timePair;
      this.timePairMapDialogActive = true;
    },

    clockOutIncompleteTimePair(timePair) {
      const approval = this.getApprovalByTimePair(timePair);
      const employee = this.getSubordinateByApprovalId(approval.id).employee;

      this.showAppConfirmDialog({
        message: `${this.$t('dialogs.confirm.clockOutEmployee', {
          firstName: approval.firstName,
          lastName: approval.lastName,
        })}`,
        confirmText: this.$t('clock.clockOut'),
        confirmHandler: () => {
          this.showAppLoader(this.$t('appLoader.clockingOut'));
          const successCallback = (responseData) => {
            this.updateApprovals({ timePairId: timePair.id, newTimePair: responseData, employee });
            this.showSnackbar({
              text: this.$t('snackbar.clockOutSuccess', { firstName: approval.firstName, lastName: approval.lastName }),
              color: constants.AppColors.SUCCESS,
            });
          };

          this.createShiftClockOut(
            timePair,
            null,
            successCallback,
            this.hideAppLoader,
            timePair.clockIn.timeZoneOffset,
          );
        },
      });
    },

    getApprovalByTimePair(timePair) {
      let matchedApproval = null;
      forEach(this.approvals, (approval) => {
        if (some(approval.summaries, (pair) => pair.id === timePair.id || pair.id === timePair.originalVersionId)) {
          matchedApproval = approval;
          return false;
        }
        return true;
      });
      return matchedApproval;
    },

    getSubordinateByApprovalId(id) {
      return find(this.subordinateEmployees, (subordinate) => subordinate.employee.id === id);
    },

    collapseNonDisplayedRows() {
      forEach(keys(this.approvalsByUsername), (username) => {
        if (this.getFilteredTimes(this.approvalsByUsername[username].summaries).length === 0) {
          const employeeRowIndex = findIndex(this.expanded, (row) => row.id === username);
          this.expanded.splice(employeeRowIndex, 1);
        }
      });
    },

    sortByPositionDescription(positionA, positionB) {
      return positionA.description.localeCompare(positionB.description);
    },

    sortByDateText(timeEntryA, timeEntryB) {
      return DateTimeUtil.dateTimeIsGreater(timeEntryB.date, timeEntryA.date);
    },

    sortByDuration(durationA, durationB) {
      if (durationA > durationB) return 1;
      if (durationA < durationB) return -1;
      return 0;
    },

    loadApprovals(statuses) {
      if (this.isMobile()) this.showAppLoader(this.$t('appLoader.loadingApprovals'));
      else this.approvalsLoading = true;

      this.approvalStatusFilter = statuses;
      this.loadSubordinateEmployees().finally(() => {
        this.loadWorkSchedules().finally(() => {
          this.loadManagerApprovals(statuses.toString()).finally(() => {
            if (this.isMobile()) this.hideAppLoader();
            else this.approvalsLoading = false;
            this.filterApprovalsByDate(this.dateRange);
          });
        });
      });
    },

    filterApprovalDates(values) {
      this.dateRange = values;
    },

    getDateRange() {
      let dateOne = new Date().toISOString();
      dateOne = dateOne.substring(0, dateOne.indexOf('T'));

      let dateTwo = new Date();
      dateTwo.setDate(dateTwo.getDate() - 28);
      dateTwo = dateTwo.toISOString();
      dateTwo = dateTwo.substring(0, dateTwo.indexOf('T'));

      return [dateTwo, dateOne];
    },

    updateTableHeaders() {
      this.headers.find(
        (tableHeader) => tableHeader.value === constants.DataTableHeaderValues.DURATION,
      ).text = this.viewAdjustedTime ? this.$t('tableHeaders.adjustedDuration') : this.$t('tableHeaders.duration');
    },
  },

  async created() {
    this.setPageTitle(this.$t('titles.approveOrReject'));
    this.updateTableHeaders();

    this.dateRange = this.getDateRange();
    await this.loadDefaultApprovalStatusFilter();
    await this.loadApprovals(this.approvalStatusConfiguration);
  },
};

</script>

<style scoped>
.sticky {
  position: sticky;
  z-index: 1;
}

.approvals-expanded-item {
  background-color: #f9f9f9;
}

/*noinspection CssUnusedSymbol*/
>>> .v-data-table-header-mobile__select {
  min-width: 20px;
  max-width: 20px;
}

>>> #approvalsTable > div.v-data-table__wrapper > table > thead.v-data-table-header-mobile > tr > th {
  border-bottom: 0;
}
</style>
